import type { ObraSummary } from '@obras/shared';
import { formatPercent } from '@obras/shared';
import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { motion, type Variants } from 'motion/react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Badge, ObraStatusBadge } from '../../components/ui/Badge.js';
import { CoverImage, CoverPlaceholder } from '../../components/ui/Cover.js';
import { Avatar, Skeleton } from '../../components/ui/Feedback.js';

/*
 * O cartão da obra e a coreografia da grade (design.md §8, Cartão de obra).
 *
 * Mora fora da página porque a régua do pé — Execução | Prazo decorrido — é
 * também a da coluna da obra no Mapa de Obras: a obra se lê do mesmo jeito nos
 * dois lugares.
 */

/** Até onde o escalonamento da grade vai: do nono cartão em diante, todos entram juntos. */
const MAX_STAGGERED = 8;
const STAGGER_STEP = 0.04;
const delayOf = (index: number): number => Math.min(index, MAX_STAGGERED) * STAGGER_STEP;

/**
 * A grade entra e sai como um bloco. Na troca de página a antiga se apaga
 * subindo um fio, em 160ms, e só então a nova entra, cartão a cartão.
 */
export const obraGrid: Variants = {
  hidden: {},
  shown: {},
  gone: { opacity: 0, y: -6, transition: { duration: 0.16, ease: EASE_OUT_QUINT } },
};

/** Cada cartão sobe 14px e assenta no seu passo da fila. */
export const obraGridItem: Variants = {
  hidden: { opacity: 0, y: 14 },
  shown: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.42, ease: EASE_OUT_QUINT, delay: delayOf(index) },
  }),
};

/** O pé cinza se desenha da esquerda para a direita depois que o cartão assentou. */
const FOOT: Variants = {
  hidden: { scaleX: 0 },
  shown: (index: number) => ({
    scaleX: 1,
    transition: { duration: 0.5, ease: EASE_OUT_QUINT, delay: delayOf(index) + 0.22 },
  }),
};

/** Uma das duas leituras do pé do cartão: o rótulo em versalete e o número. */
export function Indicador({
  rotulo,
  valor,
  title,
}: {
  rotulo: string;
  valor: string;
  title?: string | undefined;
}) {
  return (
    <div className="px-4 py-2.5" title={title}>
      <dt className="text-[10px] leading-4 font-medium tracking-[0.06em] text-ink-3 uppercase">
        {rotulo}
      </dt>
      <dd className="numeral mt-0.5 text-[15px] leading-5">{valor}</dd>
    </div>
  );
}

/**
 * Cartão da obra.
 *
 * Só o que se lê de relance: **que obra é esta** (foto e nome), **de quem é**
 * (contrato e fiscal) e **como vai**, em dois números — quanto foi executado e
 * quanto do prazo já correu ("Prazo decorrido", nunca o que falta: os dois
 * números crescem juntos e se comparam direto). Valor, próxima visita e o
 * resto ficam na tela da obra; aqui viravam ruído. O que exige reação — novidade desde a última
 * visita e situação fora do curso normal — vira selo sobre a foto, onde é
 * visto antes de qualquer leitura. Obra em andamento não ganha selo: é o
 * esperado, e etiquetar o esperado só faz a exceção sumir no meio.
 *
 * Quadrado e de tamanho fixo: foto em 16:9, nome em duas linhas reservadas
 * (o curto deixa a segunda em branco, o longo é cortado), uma linha de
 * contrato e os dois números numa régua de duas células. Todo cartão da grade
 * tem a mesma altura, haja o que houver dentro.
 *
 * Sob o ponteiro nada se mexe nas laterais: o filete continua o mesmo, um
 * reflexo atravessa a foto uma vez, na diagonal (`<CoverImage sheenOnHover>`),
 * e o pé cinza de 3px fica laranja do meio para as pontas — o mesmo gesto da
 * barra do botão. O pé fica fora da moldura, na largura inteira do cartão, para
 * cobrir também o filete das laterais.
 */
export function ObraCard({ obra, index }: { obra: ObraSummary; index: number }) {
  const hasUpdates = obra.updatesSinceLastView > 0;

  return (
    <Link to="/obras/$obraId" params={{ obraId: obra.id }} className="group flex h-full flex-col">
      <div className="flex flex-1 flex-col overflow-hidden border border-b-0 border-line bg-surface">
        <div className="relative aspect-[16/9] shrink-0 overflow-hidden">
          {obra.coverUrl ? (
            <CoverImage src={obra.coverUrl} sheenOnHover />
          ) : (
            <CoverPlaceholder busy={false} />
          )}

          {/* Os selos encostam no canto superior esquerdo, lado a lado, sem respiro. */}
          {hasUpdates || obra.status !== 'em_andamento' ? (
            <span className="absolute top-0 left-0 flex">
              {hasUpdates ? (
                <Badge tone="primary" icon={Bell} solid>
                  {obra.updatesSinceLastView}{' '}
                  {obra.updatesSinceLastView === 1 ? 'notificação' : 'notificações'}
                </Badge>
              ) : null}
              {obra.status !== 'em_andamento' ? (
                <ObraStatusBadge status={obra.status} solid />
              ) : null}
            </span>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col gap-2.5 px-4 pt-3.5 pb-3.5">
          <h3
            title={obra.name}
            className="line-clamp-2 h-10 text-[15px] leading-5 font-semibold tracking-tight"
          >
            {obra.name}
          </h3>

          <div className="flex h-[18px] items-center gap-3 text-[12px] text-ink-3">
            <span className="shrink-0 font-mono">{obra.contractNumber ?? 'sem contrato'}</span>

            {obra.responsibleFiscal ? (
              <span className="ml-auto flex min-w-0 items-center gap-1.5">
                <Avatar
                  name={obra.responsibleFiscal.name}
                  url={obra.responsibleFiscal.avatarUrl}
                  size={18}
                />
                <span className="truncate">{obra.responsibleFiscal.name}</span>
              </span>
            ) : (
              <span className="ml-auto">sem fiscal</span>
            )}
          </div>
        </div>

        {/*
         * A régua do pé: execução e prazo lado a lado, para a comparação que o
         * fiscal faz de cabeça — execução atrás do prazo é obra andando mais
         * devagar que o calendário.
         */}
        <dl className="grid shrink-0 grid-cols-2 divide-x divide-line border-t border-line">
          <Indicador rotulo="Execução" valor={formatPercent(obra.executionProgress)} />
          <Indicador
            rotulo="Prazo decorrido"
            valor={formatPercent(obra.timeProgress)}
            title="Quanto do prazo de contrato já passou"
          />
        </dl>
      </div>

      <motion.span
        aria-hidden
        variants={FOOT}
        custom={index}
        style={{ transformOrigin: 'left center' }}
        className="relative h-[3px] shrink-0 bg-line-2"
      >
        <span className="absolute inset-0 origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out-quint group-hover:scale-x-100 group-focus-visible:scale-x-100 motion-reduce:transition-none" />
      </motion.span>
    </Link>
  );
}

/** O cartão enquanto a lista não chega: as mesmas medidas, com o visor procurando o foco. */
export function ObraCardSkeleton() {
  return (
    <div aria-hidden className="flex h-full flex-col">
      <div className="flex flex-1 flex-col overflow-hidden border border-b-0 border-line bg-surface">
        <div className="relative aspect-[16/9] shrink-0">
          <CoverPlaceholder busy />
        </div>

        <div className="flex flex-1 flex-col gap-2.5 px-4 pt-3.5 pb-3.5">
          <div className="flex h-10 flex-col justify-center gap-2">
            <Skeleton className="h-3.5 w-3/4 rounded-none" />
            <Skeleton className="h-3.5 w-1/2 rounded-none" />
          </div>
          <Skeleton className="h-[18px] w-2/3 rounded-none" />
        </div>

        <div className="grid shrink-0 grid-cols-2 divide-x divide-line border-t border-line">
          {[0, 1].map((celula) => (
            <div key={celula} className="px-4 py-2.5">
              <div className="flex h-4 items-center">
                <Skeleton className="h-2.5 w-14 rounded-none" />
              </div>
              <div className="mt-0.5 flex h-5 items-center">
                <Skeleton className="h-4 w-10 rounded-none" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <span className="h-[3px] shrink-0 bg-line-2" />
    </div>
  );
}
